import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/src/lib/supabase';

export interface OverviewStats {
  lowStock: number;
  pendingActions: number;
  shiftsToday: number;
  openTasks: number;
}

const ZERO: OverviewStats = { lowStock: 0, pendingActions: 0, shiftsToday: 0, openTasks: 0 };

export function useOverviewStats(venueId: string | null, userId: string | null) {
  const [stats, setStats] = useState<OverviewStats>(ZERO);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!venueId) {
      setStats(ZERO);
      setLoading(false);
      return;
    }
    setLoading(true);
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const endOfToday = new Date(startOfToday.getTime() + 86_400_000);

    const [stockRes, pendingRes, shiftsRes, tasksRes] = await Promise.all([
      supabase.from('stock_items').select('par_level, current_level').eq('venue_id', venueId).eq('active', true),
      supabase.from('ai_pending_actions').select('id', { count: 'exact', head: true }).eq('venue_id', venueId).eq('status', 'pending'),
      supabase
        .from('shifts')
        .select('id', { count: 'exact', head: true })
        .eq('venue_id', venueId)
        .eq('published', true)
        .gte('start_time', startOfToday.toISOString())
        .lt('start_time', endOfToday.toISOString()),
      userId
        ? supabase
            .from('tasks')
            .select('id', { count: 'exact', head: true })
            .eq('venue_id', venueId)
            .eq('assigned_to', userId)
            .neq('status', 'done')
        : Promise.resolve({ count: 0, error: null } as { count: number | null; error: null }),
    ]);

    const lowStock = (stockRes.data ?? []).filter(
      (r: { par_level: number | null; current_level: number | null }) =>
        r.par_level != null && r.current_level != null && Number(r.current_level) < Number(r.par_level),
    ).length;

    setStats({
      lowStock,
      pendingActions: pendingRes.count ?? 0,
      shiftsToday: shiftsRes.count ?? 0,
      openTasks: tasksRes.count ?? 0,
    });
    setLoading(false);
  }, [venueId, userId]);

  useEffect(() => {
    void load();
  }, [load]);

  return { stats, loading, reload: load };
}
